import { useSearch } from '@faststore/sdk'
import type { Product } from 'src/components/restructure/product/types/product'
import ProductGridSkeleton from 'src/components/skeletons/ProductGridSkeleton'
import ProductCard from 'src/components/restructure/product/ProductCard'
import { useMemo } from 'react'

import { Pagination } from '../Pagination'
import { useComponentList } from '../buildComponentList'

interface ProductResultListCollectionPageV3Props {
  title: string
  totalProducts: number
  products?: Product[]
  registerPerPage: number
  type?: string
  page: number
  collectionCMSData: Array<{ id: string; name: string; data: any }> | undefined
  sphereContents?: Array<{ sphereId: string; sphereHtml: string | null }>
}

function ProductResultListCollectionPageV3({
  title,
  products = [],
  totalProducts,
  registerPerPage,
  type,
  page,
  collectionCMSData,
  sphereContents,
}: Readonly<ProductResultListCollectionPageV3Props>) {
  const {
    setPage,
    state: { term, sort, selectedFacets, base },
    itemsPerPage,
  } = useSearch()

  const gridClasses =
    'product-grid product-grid-v3 grid gap-x-2 gap-y-8 sm:gap-[10px] md:gap-5 md:gap-y-10'

  const amplitudeData = useMemo(() => {
    return {
      term,
      sort,
      page: page === 0 ? 1 : page,
      selectedFacets,
      itemsPerPage,
      totalProducts,
      base,
    }
  }, [term, sort, page, selectedFacets, itemsPerPage, totalProducts, base])

  const buildGalleryWithProducts = useMemo(() => {
    return products.map((product, idx: number) => (
      <li key={`${idx}_${product.productId}`}>
        <ProductCard
          index={idx}
          key={`${idx}_${product.productId}`}
          product={product as unknown as Product}
          amplitudeData={amplitudeData}
          type={type}
          title={title}
          shelfRef={`vitrine - ${title?.replace(/-/g, ' ')}`}
          productNameWrapper="h2"
        />
      </li>
    ))
  }, [products, amplitudeData, type, title])

  const { componentList, isMobile } = useComponentList({
    collectionCMSData,
    productsLength: products.length,
    page,
    sphereContents,
  })

  const buildGallery = useMemo(() => {
    if (!componentList || componentList.length === 0) {
      return buildGalleryWithProducts
    }

    const gallery = [...buildGalleryWithProducts]

    componentList?.forEach((componentListItem) => {
      const pageToShow = componentListItem?.pageToShow
      const shouldInsert =
        page === pageToShow || (pageToShow === 1 && page === 0)

      if (shouldInsert && componentListItem?.component) {
        return gallery.splice(
          isMobile
            ? componentListItem.positionMobile
            : componentListItem.positionDesktop,
          0,
          componentListItem.component
        )
      }
    })

    return gallery
  }, [componentList, buildGalleryWithProducts, page, isMobile])

  return (
    <div className="product-listing__results product-listing__results--contained">
      <ul className={`${gridClasses} px-4 md:px-0`}>
        <ProductGridSkeleton loading={!products.length}>
          {buildGallery || buildGalleryWithProducts}
        </ProductGridSkeleton>
      </ul>
      <Pagination
        onPageChange={setPage}
        totalCountOfRegisters={totalProducts}
        currentPage={page === 0 ? 1 : page}
        registersPerPage={registerPerPage}
      />
    </div>
  )
}

export default ProductResultListCollectionPageV3
